import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { TRADING_ELOQUENT as ELOQUENT } from 'config/constants';
import { queryClientKeys } from 'config/executors/apiInterceptor';
import { useEffect, useMemo, useState } from 'react';

/**
 * Get trader my limits
 * @returns {Promise}
 */
export function getMyLimits({ token }) {
    return ELOQUENT.MyLimits.index(undefined, token);
}

/**
 * Get total product limits
 * @param {Object} data
 * @returns {Promise}
 */
export function getTotalProducts(data) {
    return ELOQUENT.MyProductTypes.index(undefined, data.token);
}

const getBatch = (dataF, page = 1, pageSize = 10, filterFn = null) => {
    // 1. Filter first
    const filtered = filterFn ? dataF.filter(filterFn) : dataF;

    const totalItems = filtered.length;
    const totalPages = Math.ceil(totalItems / pageSize);

    // 2. Slice the batch
    const startIndex = (page - 1) * pageSize;
    const data = filtered.slice(startIndex, startIndex + pageSize);

    // 3. Return metadata
    return {
        data,
        current_page: page,
        per_page: pageSize,
        total: totalItems,
        totalPages: totalPages,
        hasNext: page < totalPages,
        hasPrev: page > 1
    };
};

export function useTotalProductsInfinite(apiCaller, initialPageParam, enabled, mapper) {
    const [list, setList] = useState([]);

    useEffect(() => {
        if (enabled) {
            (async () => {
                const [response] = await Promise.all([
                    apiCaller(getTotalProducts, {})
                ]);

                setList(response.data.data ?? []);
            })();
        }
    }, [enabled, apiCaller]);

    const api = useInfiniteQuery({
        queryKey: ['useTotalProductsInfinite', initialPageParam, list],
        initialPageParam: initialPageParam,
        enabled,
        queryFn: (ee) => {
            const dataF = ee.queryKey[1];

            return getBatch(mapper ? mapper(list, dataF) : list, dataF.page, dataF.per_page);
        },
        getNextPageParam: (lastPage, _, lastPageParam) => {
            if (!lastPage?.data?.hasNext) return undefined;

            return {
                ...lastPageParam,
                page: lastPage?.data?.hasNext
                    ? lastPage?.data?.current_page + 1
                    : lastPage?.data?.current_page
            };
        }
    });

    const finalData = useMemo(() => {
        return api.data?.pages.reduce((prev, cur) => {
            return [...prev, ...cur.data];
        }, []) ?? [];
    }, [api.data]);

    const pageData = useMemo(() => {
        const currentPage = api.data?.pages[0];

        return currentPage ?? {
            data: [],
            per_page: initialPageParam.per_page ?? 30,
            total: 0,
            current_page: 1
        };
    }, [api.data?.pages, initialPageParam.per_page]);

    return {
        ...api,
        data: finalData,
        pageData
    };
}

/**
 * Get trader cp limits
 * @param {Object} data
 * @returns {Promise}
 */
export function getTraderConterpartyLimits(data, { token }) {
    return ELOQUENT.Counterparties.index(data, token);
}

export const useGetTraderConterpartyLimitsApi = (apiCaller, data) => {
    const query = useQuery({
        queryKey: [queryClientKeys.useGetTraderConterpartyLimitsApi, data],
        queryFn: async ee => await apiCaller(getTraderConterpartyLimits, data)
    });

    return { ...query, data: query.data?.data };
};

/**
 * Get trader cp limits
 * @returns {Promise}
 */
export function getTraderConterpartyProductLimits({ token }) {
    return ELOQUENT.CounterpartiesProductTypes.index(null, token);
}

/**
 * Get all traders limits\
 * @param {Object} data
 * @returns {Promise}
 */
export function getMyTradersLimits(data) {
    return ELOQUENT.MyTraders.limits(undefined, data.token);
}

/**
 * update total product limits
 * @param {Object} id
 * @param {Object} data
 * @returns {Promise}
 */
export function updateTotalProductLimits(id, data) {
    return ELOQUENT.MyProductTypes.update(id, data);
}

/**
 * update my company limits
 * @param {Object} data
 * @returns {Promise}
 */
export function updateMyCompanyLimit(data) {
    return ELOQUENT.MyCompany.updateLimit(data);
}

/**
 * update total product limits
 * @param {Object} traderId
 * @param {Object} productTypeId
 * @param {Object} data
 * @returns {Promise}
 */
export function updateTraderProductLimit(traderId, productTypeId, data) {
    return ELOQUENT.MyTraders.updateLimit(traderId, productTypeId, data);
}

/**
 * update cp product type limits
 * @param {Object} counterpartyId
 * @param {Object} productTypeId
 * @param {Object} data
 * @returns {Promise}
 */
export function updateCounterpartyProductLimit(counterpartyId, productTypeId, data) {
    return ELOQUENT.CounterpartiesProductTypes.update(counterpartyId, productTypeId, data);
}

/**
 * update cp limits
 * @param {Object} counterpartyId
 * @param {Object} data
 * @returns {Promise}
 */
export function updateCounterpartyLimit(counterpartyId, data) {
    return ELOQUENT.Counterparties.update(counterpartyId, data);
}
